/**
 * The two Bulk_Edit calls (`complan-governance` R10.4, R10.7, R10.9, R10.15).
 *
 * ## Why preview is a mutation, not a query
 *
 * A preview reads, so a `useQuery` looks right - and would be wrong. R10.15 makes the
 * preview part of *one interaction*: the reader selects, previews, reads the before/after
 * values, and applies with the token that preview issued. A query would be cached and
 * refetched on its own schedule, so the token in hand could silently become one from a
 * different read of different data, and the apply would carry versions nobody looked at.
 * Modelling it as a mutation keeps the preview an event the reader caused, which is what
 * R10.15 requires it to be.
 *
 * ## Applying invalidates the list, because records really changed
 *
 * The opposite of the Watchout writes: a Bulk_Edit *is* a change to the Initiatives, so
 * the list, the calendar and the dashboards are all stale afterwards. Invalidating by the
 * broadest prefixes rather than per record, because R10.10 can touch hundreds.
 */
import {
  bulkEditApplyResponseSchema,
  bulkEditPreviewResponseSchema,
  type BulkEditApplyRequest,
  type BulkEditApplyResponse,
  type BulkEditPreviewRequest,
  type BulkEditPreviewResponse,
} from '@complan/contracts';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { apiFetch } from '@/shared/api/client';

/** `POST /api/bulk-edit/preview` (R10.4). */
export async function fetchBulkEditPreview(
  request: BulkEditPreviewRequest,
): Promise<BulkEditPreviewResponse> {
  const body = await apiFetch<unknown>('/api/bulk-edit/preview', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return bulkEditPreviewResponseSchema.parse(body);
}

/** See {@link fetchBulkEditPreview} and this module's header for why this is a mutation. */
export function useBulkEditPreview(): UseMutationResult<
  BulkEditPreviewResponse,
  unknown,
  BulkEditPreviewRequest
> {
  return useMutation({ mutationFn: fetchBulkEditPreview });
}

/** `POST /api/bulk-edit/apply` (R10.7, R10.9, R10.11, R10.15). */
export async function applyBulkEdit(
  request: BulkEditApplyRequest,
): Promise<BulkEditApplyResponse> {
  const body = await apiFetch<unknown>('/api/bulk-edit/apply', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return bulkEditApplyResponseSchema.parse(body);
}

/** See {@link applyBulkEdit}. */
export function useBulkEditApply(): UseMutationResult<
  BulkEditApplyResponse,
  unknown,
  BulkEditApplyRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: applyBulkEdit,
    onSuccess: () => {
      // Hundreds of records may have changed (R10.6's cap is configurable up to 200 by
      // default), so every reader of Initiative data is stale. Prefixes, not ids.
      void client.invalidateQueries({ queryKey: ['initiative-list'] });
      void client.invalidateQueries({ queryKey: ['initiative-detail'] });
      void client.invalidateQueries({ queryKey: ['calendar'] });
      void client.invalidateQueries({ queryKey: ['governance'] });
    },
  });
}
